import { FC, useContext, useEffect, useState } from "react";
import { FormProps } from ".";
import Button from "../common/button";
import { AuthContext, UserPayload } from "@/context/AuthContext";
import { verifyFormEN } from "@/assets/dictionaries/en/sign-in-content";
import { verifyFormES } from "@/assets/dictionaries/es/sign-in-content";

enum Alerts {
  NO_ALERT,
  USER_ALREADY_EXISTS,
  SERVER_ERROR,
  WRONG,
}
const SERVER_API_URL = process.env.NEXT_PUBLIC_SERVER_API_URL;

const VerifyForm: FC<FormProps> = ({ changeForm, onSuccess }) => {
  const { logIn } = useContext(AuthContext);
  const [disableButton, setDisableButton] = useState<boolean>(false);
  const [formData, setFormData] = useState({
    email: "",
    verification_code: "",
  });
  const [formErrors, setFormErrors] = useState({
    email: "",
    verification_code: "",
  });
  const [showAlert, setShowAlert] = useState(Alerts.NO_ALERT);

  const [verifyFormContent, setVerifyFormContent] = useState(verifyFormEN); // Default to English

  useEffect(() => {
    // Get the language from localStorage
    const lang = localStorage.getItem("selectedOption") || "EN"; // Default to EN if not found

    // Set the content based on the language
    switch (lang.toUpperCase()) {
      case "ES":
        setVerifyFormContent(verifyFormES);
        break;
      case "EN":
      default:
        setVerifyFormContent(verifyFormEN);
        break;
    }
  }, []); // Only run on mount

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setFormData({ ...formData, [e.target.id]: e.target.value });
    setFormErrors({ ...formErrors, [e.target.id]: "" }); // Remove error on type
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const errors = {
      email: "",
      verification_code: "",
    };

    let register_errors = 0;

    if (!formData.email) {
      errors.email = verifyFormContent.error_email_1;
      register_errors = register_errors + 1;
    } else {
      if (!formData.email.match(/^[\w-.]+(\+[\w-]*)?@([\w-]+\.)+[\w-]{2,4}$/)) {
        errors.email = verifyFormContent.error_email_2;
        register_errors = register_errors + 1;
      }
    }

    if (!formData.verification_code) {
      errors.verification_code = verifyFormContent.error_verify_code;
      register_errors = register_errors + 1;
    }

    if (register_errors != 0) {
      setFormErrors(errors);
      return;
    }

    setDisableButton(true);

    const credentials = {
      email: formData.email,
      verification_code: formData.verification_code,
    };

    const verifyUser = async () => {
      try {
        const res = await fetch(`${SERVER_API_URL}/users/verify/user`, {
          body: JSON.stringify(credentials),
          headers: {
            "Content-Type": "application/json",
          },
          method: "PUT",
        });

        if (res.status >= 200 && res.status <= 299) {
          const data = await res.json();
          setDisableButton(false);
          onSuccess();
        } else {
          const error = await res.json();
          const detail: string = error["detail"];
          setDisableButton(false);
          if (detail.includes("User") && res.status == 500) {
            setShowAlert(Alerts.USER_ALREADY_EXISTS);
          } else if (res.status == 400) {
            setShowAlert(Alerts.WRONG);
          } else {
            setShowAlert(Alerts.SERVER_ERROR);
          }
        }
      } catch (e) {
        setShowAlert(Alerts.SERVER_ERROR);
        setDisableButton(false);
      }
    };

    verifyUser();
  };

  return (
    <div>
      <p className="text-3xl font-bold text-center">
        {verifyFormContent.verify_title}
      </p>
      <p className="mt-2 text-xl text-slate-400 mb-8 text-center">
        {verifyFormContent.verify_desc}
      </p>

      <form className="w-full flex flex-col gap-2" onSubmit={handleSubmit}>
        <label className="text-slate-400 font-bold flex flex-col">
          {verifyFormContent.verify_email_label}
          <input
            className="border-2 rounded-lg p-2 w-full"
            placeholder={verifyFormContent.verify_email}
            type="text"
            id="email"
            value={formData.email}
            onChange={handleChange}
          />
          <p
            className={
              formErrors.email ? "text-red-500" : "text-transparent select-none"
            }
          >
            {formErrors.email || "z"}
          </p>
        </label>
        <label className="text-slate-400 font-bold flex flex-col">
          {verifyFormContent.verify_code_label}
          <input
            className="border-2 rounded-lg p-2 w-full"
            placeholder={verifyFormContent.verify_code}
            type="text"
            id="verification_code"
            value={formData.verification_code}
            onChange={handleChange}
            autoFocus={true}
          />
          <p
            className={
              formErrors.verification_code
                ? "text-red-500"
                : "text-transparent select-none"
            }
          >
            {formErrors.verification_code || "z"}
          </p>
        </label>
        <Button size="large" type="submit" disabled={disableButton}>
          {verifyFormContent.button_verify}
        </Button>
      </form>

      {showAlert !== Alerts.NO_ALERT && (
        <div className="mt-8 p-4 text-red-400 bg-red-100 border border-red-400 rounded-md">
          <p>
            {showAlert == Alerts.USER_ALREADY_EXISTS
              ? verifyFormContent.alert_1
              : showAlert == Alerts.WRONG
                ? verifyFormContent.alert_2
                : verifyFormContent.alert_3}
          </p>
        </div>
      )}
    </div>
  );
};

export default VerifyForm;
